/**
 * Sidebar Component
 *
 * Collapsible sidebar navigation for Odin.
 * - 56px collapsed, 240px expanded
 * - Expands on hover or when pinned
 * - Pin state persisted in localStorage
 * - Smooth 200ms transitions
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Pin, PinOff } from 'lucide-react'
import SidebarNav from './SidebarNav'

// localStorage key for pin state
const SIDEBAR_PIN_KEY = 'odin-sidebar-pinned'

// Sidebar dimensions
const SIDEBAR_COLLAPSED_WIDTH = 56
const SIDEBAR_EXPANDED_WIDTH = 240

export default function Sidebar({ user, onLogout }) {
  const navigate = useNavigate()
  const location = useLocation()

  // Pin state - persisted in localStorage
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem(SIDEBAR_PIN_KEY)
    return stored === 'true'
  })

  // Hover state for expand on hover
  const [isHovered, setIsHovered] = useState(false)

  // Derived expanded state
  const isExpanded = isPinned || isHovered

  // Toggle pin state and persist
  const togglePin = useCallback(() => {
    setIsPinned(prev => {
      const newValue = !prev
      localStorage.setItem(SIDEBAR_PIN_KEY, String(newValue))
      window.dispatchEvent(new CustomEvent('sidebar-pin-change', { detail: { pinned: newValue } }))
      return newValue
    })
  }, [])

  // Navigate to home on logo click
  const handleLogoClick = useCallback(() => {
    navigate('/')
  }, [navigate])

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen z-40',
        'bg-white border-r border-border',
        'flex flex-col',
        'transition-all duration-200 ease-out'
      )}
      style={{
        width: isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo Section */}
      <div
        className={cn(
          'flex items-center h-16 border-b border-border',
          'cursor-pointer hover:bg-accent/50 transition-colors',
          isExpanded ? 'px-4 justify-start gap-3' : 'justify-center'
        )}
        onClick={handleLogoClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleLogoClick()}
        aria-label="Go to home"
      >
        <img
          src="/odinlogo.png"
          alt="Odin Logo"
          className="w-10 h-10 object-contain flex-shrink-0"
        />
        {isExpanded && (
          <div className="flex flex-col overflow-hidden">
            <span className="text-lg font-semibold text-foreground truncate">Odin</span>
            <span className="text-xs text-muted-foreground truncate">Farm Management</span>
          </div>
        )}
      </div>

      {/* Pin Button */}
      <button
        onClick={togglePin}
        className={cn(
          'absolute top-4 transition-all duration-200',
          isExpanded ? 'right-3 opacity-100' : 'right-1 opacity-0 pointer-events-none',
          'p-1.5 rounded-md',
          'hover:bg-accent text-muted-foreground hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
        aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
        title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
      >
        {isPinned ? (
          <Pin className="w-4 h-4" />
        ) : (
          <PinOff className="w-4 h-4" />
        )}
      </button>

      {/* Navigation */}
      <SidebarNav
        isExpanded={isExpanded}
        currentPath={location.pathname}
        user={user}
        onLogout={onLogout}
      />
    </aside>
  )
}

// Export dimensions for use in PageLayout
export { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH, SIDEBAR_PIN_KEY }
